/**
 * components/instructor/PolicyImageSection.jsx
 * =============================================
 * Manage tab: the instructor's policy reminder image — the optional animated
 * image students see on the policy violation modal.
 *
 * Drops into ManageTab alongside QueueMessagesSection. Uses the same raw-fetch
 * pattern as the roster CSV import for the multipart POST, and apiFetch for
 * everything else.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

export default function PolicyImageSection() {
  const { apiFetch } = useAuth();

  const [state,   setState]   = useState(null);   // describe() payload
  const [alt,     setAlt]     = useState('');
  const [file,    setFile]    = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [msg,     setMsg]     = useState(null);   // { text, isError }
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    try {
      const res = await apiFetch('/api/policy-image');
      if (res.ok) {
        const data = await res.json();
        setState(data);
        setAlt(data.image_alt || '');
      }
    } catch { /* section just stays empty */ }
  };

  const flash = (text, isError = false) => {
    setMsg({ text, isError });
    setTimeout(() => setMsg(null), 6000);
  };

  const onPick = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    // Local preview so they see what they picked before committing. Revoked on
    // the next pick to avoid leaking object URLs.
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(f); });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file)        { flash('Choose a GIF first.', true); return; }
    if (!alt.trim())  { flash('Please describe the image before uploading.', true); return; }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt', alt.trim());
      const res = await fetch('/api/policy-image', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('gradiance_token')}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setState(data);
        setFile(null);
        setPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
        if (fileRef.current) fileRef.current.value = '';
        flash(`Saved. Stored at ${data.width}×${data.height}, ${data.frames} frame${data.frames === 1 ? '' : 's'}.`);
      } else {
        flash(data.detail || 'Upload failed.', true);
      }
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/policy-image', { method: 'DELETE' });
      const data = await res.json();
      if (res.ok) {
        setState(data);
        setAlt('');
        flash('Removed. Students now see a text-only message.');
      } else {
        flash(data.detail || 'Could not remove the image.', true);
      }
    } catch (err) { flash(err.message, true); }
    finally { setBusy(false); }
  };

  if (!state) return null;

  if (!state.may_upload) {
    return (
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Policy Reminder Image</h3>
        <p className={styles.empty}>
          The image is set by the course&apos;s instructor, so it carries their name
          on the attribution line students see.
        </p>
      </section>
    );
  }

  const maxMb = Math.round(state.max_bytes / 1024 / 1024);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Policy Reminder Image</h3>

      <p className={styles.policyHint}>
        An optional animated GIF shown to your students when they try something
        the queue does not allow — joining twice, joining outside your window,
        or past the daily limit. Leave it empty and they see the message on its
        own. There is no default image.
      </p>

      {msg && (
        <div className={msg.isError ? styles.error : styles.success}>{msg.text}</div>
      )}

      {/* An admin takedown, and why. Shown here rather than emailed so it is
          attached to the thing it is about. */}
      {state.blocked && (
        <div className={styles.error} role="alert">
          <strong>Hidden from students by an administrator.</strong>
          <div>{state.blocked_reason}</div>
          <div className={styles.policyHint}>
            Uploading a different image clears this.
          </div>
        </div>
      )}

      {state.has_image && (
        <div className={styles.policyImageCurrent}>
          <img
            src={state.image_url}
            alt={state.image_alt || ''}
            className={styles.policyImagePreview}
          />
          <div>
            <div className={styles.policyImageMeta}>
              {state.blocked ? 'Currently hidden' : 'Currently live for your students'}
            </div>
            <div className={styles.policyHint}>{state.image_alt}</div>
            <button
              type="button"
              className="btn-ghost"
              onClick={handleRemove}
              disabled={busy}
            >
              Remove image
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleUpload} className={styles.form}>
        <div className={styles.field}>
          <label htmlFor="policy-image-file">
            {state.has_image ? 'Replace with' : 'Choose a GIF'}
          </label>
          <input
            id="policy-image-file"
            ref={fileRef}
            type="file"
            accept="image/gif"
            onChange={onPick}
          />
          <span className={styles.policyHint}>
            GIF only, up to {maxMb} MB, {state.max_dim}×{state.max_dim} and{' '}
            {state.max_frames} frames. Larger images are scaled down when saved.
          </span>
        </div>

        {preview && (
          <div className={styles.policyImageCurrent}>
            <img src={preview} alt="" className={styles.policyImagePreview} />
            <div className={styles.policyImageMeta}>Not saved yet</div>
          </div>
        )}

        <div className={styles.field}>
          <label htmlFor="policy-image-alt">Describe the image</label>
          <input
            id="policy-image-alt"
            type="text"
            value={alt}
            maxLength={200}
            placeholder="e.g. Your instructor waving a finger, saying nah ah ah"
            onChange={(e) => setAlt(e.target.value)}
          />
          {/* Required, and worth saying why rather than just rejecting it. */}
          <span className={styles.policyHint}>
            Read aloud to students using a screen reader. Describe the gesture
            and any words in the image — you are the only one who can.
          </span>
        </div>

        <div className={styles.policyHint}>
          Students see a line under the image noting it came from you and not
          from Gradiance. Anything you upload is visible to every student in
          your courses and is subject to your institution&apos;s conduct policies.
        </div>

        <button type="submit" className="btn-primary" disabled={busy || !file}>
          {busy ? 'Saving…' : state.has_image ? 'Replace image' : 'Save image'}
        </button>
      </form>
    </section>
  );
}
